import {Component, computed, input, output, signal, inject} from '@angular/core';
import {MaximumWorkingHours} from '../../../classes/models/employee/MaximumWorkingHours';
import {TableHeader} from '../../../shared/classes/TableHeader';
import moment from 'moment';
import {TableSort} from '../../../shared/classes/TableSort';
import {MaxWorkingHoursFormComponent} from './max-working-hours-form/max-working-hours-form.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MaxWorkingHoursDeleteComponent} from './max-working-hours-delete/max-working-hours-delete.component';
import {DataService} from '../../../services/store/data.service';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';

/**
 * A list of maximum working hours' data to be showed in the table. Most field names are used for sorting the table - it is defined in
 * {@link MaximumWorkingHoursComponent.headers}.
 */
class TableData {
  /** The maximum number of hours an employee can work in one week. */
  hours: number;
  /** The Monday from which maximum working hours takes effect. */
  startDate: string;
  /** The difference between contract hours and maximum hours. */
  diffToContract: number;

  constructor(maximumWorkingHours: MaximumWorkingHours, contractHours: number) {
    this.hours = maximumWorkingHours.hours;
    this.startDate = maximumWorkingHours.startDate;
    this.diffToContract = contractHours - maximumWorkingHours.hours;
  }
}

/**
 * This component handles displaying and editing maximum working hours settings.
 */
@Component({
  selector: 'app-maximum-working-hours',
  templateUrl: './maximum-working-hours.component.html',
  styleUrls: ['./maximum-working-hours.component.less'],
  imports: [SHARED_IMPORTS]
})
export class MaximumWorkingHoursComponent {
  private modalService = inject(NgbModal);
  private dataService = inject(DataService);

  /** Identifiers of an employee and position. */
  readonly employeePositionId = input<EmployeePositionId>(undefined);
  /** Employee's contracted hours. */
  readonly contractHours = input<number>();
  /** A list of employee's maximum working hours containing settings for multiple weeks. */
  readonly maximumWorkingHours = input<MaximumWorkingHours[]>([]);
  /** An event to refresh employee page data. */
  readonly getEmployeePageDataEvent = output();

  /** A column and direction for table to be sorted. */
  tableSortEvent = signal<TableSort>(undefined);

  /** Sortable headers of maximum working hours table. */
  headers: TableHeader[] = [
    new TableHeader('Available', 'hours', true),
    new TableHeader('From', 'startDate', true),
    new TableHeader('Difference To Contract', 'diffToContract', true),
    new TableHeader('', '', false)
  ];

  /** A computed list of maximum working hours' data to be showed in the table. */
  tableData = computed<TableData[]>(() => {
    const maxHoursList = this.maximumWorkingHours();
    const contract = this.contractHours();
    return maxHoursList
            .map(maxHours => new TableData(maxHours, contract))
            .sort((previous, current) => moment(current.startDate).isBefore(previous.startDate) ? 1 : -1);
  });

  /**
   * Sorts the table by the given column in the given direction.
   * @param tableSortEvent a column and direction, in which table should be sorted.
   */
  onSort(tableSortEvent: TableSort): void {
    this.tableSortEvent.set(tableSortEvent);
  }

  /**
   * Checks if data is allowed to edit at the given date.
   * @param date the date on which data might be edited.
   */
  canEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Opens a modal window to create a new maximum working hours setting.
   */
  openCreate(): void {
    const modalRef = MaxWorkingHoursFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      minHours: this.contractHours(),
      previousMaximumWorkingHours: undefined,
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to update a maximum working hours setting.
   */
  openUpdate(maximumWorkingHours: MaximumWorkingHours): void {
    const modalRef = MaxWorkingHoursFormComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      minHours: this.contractHours(),
      previousMaximumWorkingHours: maximumWorkingHours,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to delete an existing maximum working hours setting.
   * @param date date starting from which maximum working hours will be deleted.
   */
  openDelete(date: string): void {
    const modalRef = MaxWorkingHoursDeleteComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      maximumWorkingHours: this.maximumWorkingHours().find(maxHours => maxHours.startDate === date)
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }
}